/**
 * ENG-5815 — data-driven renderer for native (stdio) MCP server entries.
 *
 * Why this exists
 * ---------------
 * `buildMcpJson()` (claudecode/index.ts) and the parallel
 * `writeIntegrations()` path used to hand-roll an `if (definition_id ===
 * 'qmd')` / `if (xero) { ... }` / `if (cloudBroker) { ... }` block per
 * integration. Adding a new native MCP server (e.g. AWS) required a core
 * release and a touch to both call sites. There was no way for a new
 * integration definition to ship its own MCP entry as data.
 *
 * This module is the data-driven path. An `IntegrationDefinition` can
 * carry an optional `nativeMcp: NativeMcpSpec` that fully describes the
 * MCP entry it would have hand-rolled — command, args, and an env map.
 * The renderer (`buildNativeMcpEntry`) resolves a small templating
 * vocabulary into either literal strings baked into the JSON, or
 * `${PLACEHOLDER}` tokens that Claude Code substitutes from the MCP
 * spawn env at launch time.
 *
 * Scope
 * -----
 * The issue calls for one-handler-at-a-time migration with hardcoded
 * fallbacks left in place until each migration is verified. This module
 * is the mechanism. ENG-5815's first migration is `qmd` (simplest — no
 * env, just `qmd mcp`). `xero`, `postiz`, and `cloud-broker` have
 * conditional logic (broker-mode toggles, optional env keys) that needs
 * a richer schema; they stay hardcoded for now and will migrate as
 * follow-ups.
 *
 * Templating vocabulary
 * ---------------------
 * Inside `command`, `args[i]`, and `env[k]` values, the renderer
 * recognises these tokens:
 *
 *   - `{{agent_id}}`              → resolved at render time to the agent's UUID
 *   - `{{agent_code_name}}`       → resolved at render time to the code_name
 *   - `{{integration_id}}`        → resolved to this integration row's id, or '' if absent
 *   - `{{process_env.NAME}}`      → resolved to process.env[NAME] ?? ''
 *   - `{{empty_if_no_env.NAME}}`  → resolved like process_env.NAME but
 *                                   the key is OMITTED from the env map
 *                                   when the value would be empty (used
 *                                   to avoid setting empty AGT_HOST etc.
 *                                   that would override defaults)
 *
 * Anything else — including bare `${AGT_HOST}` style — passes through
 * untouched. Claude Code interprets `${...}` at MCP-spawn time against
 * its own environment; that's the existing contract every current
 * integration relies on.
 *
 * Why two separate substitution layers? Because some env values must
 * be baked at render time (AGT_AGENT_ID is the agent's UUID — a stable
 * identity attribute), others must be late-bound (AGT_API_KEY rotates;
 * the renderer doesn't know what it'll be). The `{{...}}` form is
 * Augmented-side render-time resolution; the `${...}` form is Claude
 * Code's spawn-time substitution. Both can coexist in the same env
 * value if a future integration ever needs it.
 */

import type { ResolvedIntegration } from '../types/integration.js';

/**
 * Describes a native (stdio) MCP server entry. Mirrors the structure
 * Claude Code expects under `.mcp.json#/mcpServers/<key>` so the spec
 * maps to the rendered JSON 1:1 (modulo templating).
 */
export interface NativeMcpSpec {
  /**
   * The key the entry lands under in `mcpServers`. When omitted, the
   * caller's chosen key (typically the integration's `definition_id`)
   * is used.
   */
  key?: string;
  /** Executable command (`node`, `npx`, `uvx`, `qmd`, …). Templated. */
  command: string;
  /** Argv after `command`. Each entry templated. */
  args: string[];
  /**
   * Env vars to set on the MCP child. Each VALUE templated; keys are
   * literal. Omit the field entirely (rather than passing `{}`) to
   * suppress an `env` property in the rendered JSON — some servers
   * (qmd today) deliberately have no env block, and tests pin that.
   */
  env?: Record<string, string>;
}

/**
 * Render-time context the templating layer resolves against.
 */
export interface NativeMcpRenderContext {
  /** The agent UUID — `{{agent_id}}` resolves to this. */
  agentId: string;
  /** The agent `code_name` — `{{agent_code_name}}` resolves to this. */
  agentCodeName: string;
  /**
   * The integration row this spec is being rendered for (when
   * relevant). `{{integration_id}}` resolves to `integration.id ?? ''`.
   * Omitted for definition-level renders that aren't tied to a row
   * (rare; today every native MCP is paired with a row).
   */
  integration?: ResolvedIntegration;
}

/**
 * Render a `NativeMcpSpec` into the `.mcp.json` entry shape, resolving
 * the templating vocabulary above. Pure function — no I/O, no
 * `process.env` reads outside the explicit `{{process_env.NAME}}` path.
 *
 * The returned object's key set deliberately matches Claude Code's
 * .mcp.json schema (`command`, `args`, optional `env`). Callers splice
 * it directly into `mcpServers[key]`.
 */
export function buildNativeMcpEntry(
  spec: NativeMcpSpec,
  ctx: NativeMcpRenderContext,
): { command: string; args: string[]; env?: Record<string, string> } {
  // `empty_if_no_env` has whole-value omit semantics — it makes sense
  // only as an env *key* the renderer can drop. In `command` and any
  // `args[i]` position, dropping the value would leave a structurally
  // broken entry, so we fail fast at render time rather than serialize
  // an invalid `.mcp.json` (CodeRabbit ENG-5815 review #1598).
  const resolvedCommand = resolveTemplate(spec.command, ctx);
  if (resolvedCommand.omit) {
    throw new Error(
      'NativeMcpSpec: empty_if_no_env is only valid in env values (not in `command`)',
    );
  }
  const command = resolvedCommand.value;
  const args = spec.args.map((a, i) => {
    const resolved = resolveTemplate(a, ctx);
    if (resolved.omit) {
      throw new Error(
        `NativeMcpSpec: empty_if_no_env is only valid in env values (not in args[${i}])`,
      );
    }
    return resolved.value;
  });

  if (spec.env === undefined) {
    return { command, args };
  }

  const env: Record<string, string> = {};
  for (const [k, raw] of Object.entries(spec.env)) {
    const { value, omit } = resolveTemplate(raw, ctx);
    if (omit) continue;
    env[k] = value;
  }
  return { command, args, env };
}

interface ResolvedValue {
  value: string;
  /** When true, the caller should omit this key entirely (used by `empty_if_no_env`). */
  omit: boolean;
}

/**
 * Resolve `{{token}}` substitutions within a single string. Returns
 * `omit: true` only when the input contains a `{{empty_if_no_env.NAME}}`
 * token AND `process.env[NAME]` is empty — in that case the env key
 * gets dropped rather than emitted as an empty string. (Mixing
 * `empty_if_no_env` with literal text in the same value triggers a
 * throw: the omit semantics make no sense for a partial substitution.)
 */
function resolveTemplate(
  input: string,
  ctx: NativeMcpRenderContext,
): ResolvedValue {
  const TOKEN = /\{\{([^}]+)\}\}/g;

  // Static check first: `empty_if_no_env` has whole-value omit
  // semantics, so it must appear as the SOLE content of the value —
  // mixing with literal text, with another token, or with a second
  // `empty_if_no_env` is an ambiguous spec. Throw at render time so
  // the bad catalog entry surfaces in tests rather than at agent
  // runtime. Done outside the substitution pass so the guard fires
  // regardless of whether the env var happens to be set in this
  // process. CodeRabbit ENG-5815 review #1598 tightened this to also
  // reject `{{empty_if_no_env.A}}{{empty_if_no_env.B}}`.
  const hasEmptyIfNoEnv = /\{\{\s*empty_if_no_env\./.test(input);
  const isWholeValueEmptyIfNoEnv = /^\{\{\s*empty_if_no_env\.[^}]+\}\}$/.test(input);
  if (hasEmptyIfNoEnv && !isWholeValueEmptyIfNoEnv) {
    throw new Error(
      `NativeMcpSpec: empty_if_no_env must be the sole content of the value, never mixed with literal text or other tokens (value: ${JSON.stringify(input)})`,
    );
  }

  let omit = false;
  const value = input.replace(TOKEN, (whole, expr: string) => {
    const trimmed = expr.trim();
    if (trimmed === 'agent_id') return ctx.agentId;
    if (trimmed === 'agent_code_name') return ctx.agentCodeName;
    if (trimmed === 'integration_id') return ctx.integration?.id ?? '';
    if (trimmed.startsWith('process_env.')) {
      const name = trimmed.slice('process_env.'.length);
      return process.env[name] ?? '';
    }
    if (trimmed.startsWith('empty_if_no_env.')) {
      const name = trimmed.slice('empty_if_no_env.'.length);
      const v = process.env[name] ?? '';
      if (v.length === 0) {
        omit = true;
        return '';
      }
      return v;
    }
    // Unknown `{{...}}` token — pass through literally so a typo in a
    // catalog spec is visible (the rendered JSON will carry `{{typo}}`
    // rather than silently collapse). Future: surface as a lint
    // diagnostic on definition load.
    return whole;
  });

  return { value, omit };
}
